import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { isValidDate } from '@core';

import { userAtom } from '../global/user';

export type ActivityFilter = {
    keywords: string;
    startDate: Date | null;
    endDate: Date | null;
    favorites: boolean;
    clubId?: number;
    clubTypeId?: number;
    hasClub?: boolean;
    tags?: number[];
    studentId: string;
};

type StoredActivityFilter = ActivityFilter & { userId: string };

const ACTIVITY_FILTERS_KEY = 'activity-filters';

export const DEFAULT_ACTIVITY_FILTERS: StoredActivityFilter = {
    keywords: '',
    startDate: null,
    endDate: null,
    favorites: false,
    clubId: undefined,
    clubTypeId: undefined,
    hasClub: undefined,
    tags: undefined,
    studentId: '',
    userId: '',
};

const sessionStorageAdapter = createJSONStorage<StoredActivityFilter>(() => sessionStorage, {
    replacer: (key, value) => {
        if ((key === 'startDate' || key === 'endDate') && value instanceof Date) {
            if (!isValidDate(value)) {
                return null;
            }

            return value.toISOString();
        }

        return value;
    },
    reviver: (key, value) => {
        if ((key === 'startDate' || key === 'endDate') && typeof value === 'string') {
            const parsedDate = new Date(value);
            return isValidDate(parsedDate) ? parsedDate : null;
        }

        return value;
    },
});

export const activityFilterAtom = atomWithStorage<StoredActivityFilter>(
    ACTIVITY_FILTERS_KEY,
    DEFAULT_ACTIVITY_FILTERS,
    sessionStorageAdapter,
);

export const updateActivityFilterAtom = atom(null, (get, set, updates: Partial<StoredActivityFilter>) => {
    set(activityFilterAtom, {
        ...get(activityFilterAtom),
        ...updates,
    });
});

export const resetActivityFilterAtom = atom(null, (get, set) => {
    const user = get(userAtom);

    set(activityFilterAtom, {
        ...DEFAULT_ACTIVITY_FILTERS,
        userId: user!.id,
    });
});
